import { useState, useMemo, useEffect } from "react";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Stores from "@/pages/Stores";
import BaseInfo from "@/pages/BaseInfo";
import StoreDetail from "@/pages/StoreDetail";
import {
  Store, Database, LayoutGrid, BarChart3, Users, Building2, TrendingUp, X as XIcon, Search, Eye,
  AlertTriangle,
} from "lucide-react";
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";

const fmtNum = v => v == null || isNaN(v) ? "—" : v.toLocaleString("fr-FR");
const fmtPct = v => v == null || isNaN(v) ? "—" : `${Math.round(v * 100)}%`;
const fmtEur = v => v == null || isNaN(v) ? "—" : Math.round(v).toLocaleString("fr-FR") + " €";
const fmtEurShort = v => {
  if (v == null || isNaN(v)) return "—";
  return Math.round(v).toLocaleString("fr-FR").replace(/\u202F/g, " ").replace(/,/g, " ") + " €";
};
const fmtDate = d => d ? new Date(String(d).slice(0, 10)).toLocaleDateString("fr-FR") : "—";
const PIE_COLORS = ["#2563EB", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899", "#0EA5E9", "#F43F5E", "#84CC16", "#EAB308", "#6366F1"];

/* ── Filter bar (common to all 4 tabs) ── */
function InfoMagFilterBar({ meta, f, setF }) {
  const filtered = (opts, key) => {
    if (!f.drs?.length && !f.secteurs?.length && !f.enseignes?.length) return opts || [];
    return (opts || []).filter(o => {
      // meta.corners entries have {corner, dr, secteur, bm, enseigne}
      return true;
    });
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap gap-2 items-end" data-testid="infomag-filterbar">
      <div className="relative flex-1 min-w-[200px]">
        <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
        <input value={f.search || ""} onChange={e => setF(p => ({ ...p, search: e.target.value }))}
               placeholder="Rechercher un corner…"
               data-testid="infomag-flt-search"
               className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-slate-200 text-[12px] outline-none focus:ring-2 focus:ring-blue-100"/>
      </div>
      {[
        { k: "drs",       label: "DR",       opts: meta?.drs },
        { k: "bms",       label: "BM",       opts: meta?.bms },
        { k: "secteurs",  label: "Secteur",  opts: meta?.secteurs },
        { k: "corners",   label: "Corner",   opts: (meta?.corners || []).map(c => c.corner) },
        { k: "enseignes", label: "Enseigne", opts: meta?.enseignes },
        { k: "marques",   label: "Marque",   opts: meta?.marques },
        { k: "annees",    label: "Année",    opts: (meta?.years || meta?.annees || []).map(String) },
        { k: "mois",      label: "Mois",     opts: meta?.months || meta?.mois },
      ].map(s => (
        <FilterMultiSelect key={s.k} label={s.label} placeholder={s.label} testid={`infomag-flt-${s.k}`} size="sm" minWidth={120}
                           options={s.opts || []} value={f[s.k] || []} onChange={v => setF(p => ({ ...p, [s.k]: v }))}/>
      ))}
      {(Object.values(f).some(v => (Array.isArray(v) ? v.length : v))) && (
        <button onClick={() => setF({})} data-testid="infomag-flt-reset"
                className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 px-2 py-1 rounded hover:bg-slate-100">
          Réinitialiser
        </button>
      )}
    </div>
  );
}

/* ── Tab 1 : Récap (dynamic summary from indicators) ── */
function RecapTab({ data }) {
  const s = data?.summary || {};
  const byEns = data?.by_enseigne || [];
  const byDR = data?.by_dr || [];
  const byBM = data?.by_bm || [];
  const evo = data?.monthly_count || [];
  const teams = data?.teams || [];
  const kpis = [
    { icon: Store, label: "Total magasins", value: fmtNum(s.n_corners), color: "text-blue-600 bg-blue-50" },
    { icon: Users, label: "Total ETP", value: fmtNum(s.total_etp, 1), color: "text-emerald-600 bg-emerald-50" },
    { icon: Building2, label: "Enseignes", value: fmtNum(byEns.length), color: "text-purple-600 bg-purple-50" },
    { icon: Users, label: "DR actifs", value: fmtNum(byDR.length), color: "text-amber-600 bg-amber-50" },
    { icon: Users, label: "BM actifs", value: fmtNum(byBM.length), color: "text-cyan-600 bg-cyan-50" },
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="infomag-recap-kpis">
        {kpis.map(k => (
          <div key={k.label} className="bg-white rounded-xl border border-slate-200 p-4 shadow-[0_1px_3px_rgba(15,23,42,.04)]">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{k.label}</span>
              <span className={`w-7 h-7 rounded-md grid place-items-center ${k.color}`}><k.icon className="w-3.5 h-3.5"/></span>
            </div>
            <div className="text-[22px] font-bold text-slate-900 tabular-nums">{k.value}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-[13px] font-bold text-slate-900 mb-2">Répartition par enseigne</h3>
          <div className="h-52">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={byEns} dataKey="count" nameKey="label" innerRadius={45} outerRadius={78} paddingAngle={2}>
                  {byEns.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]}/>)}
                </Pie>
                <Tooltip formatter={v => `${v} magasins`}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1 text-[11px] mt-1 max-h-24 overflow-auto">
            {byEns.map((e, i) => (
              <div key={e.label} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}/>
                <span className="flex-1 truncate text-slate-700">{e.label || "—"}</span>
                <span className="font-semibold tabular-nums">{e.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-[13px] font-bold text-slate-900 mb-2">Magasins par DR</h3>
          <div className="h-52">
            <ResponsiveContainer>
              <BarChart data={byDR} margin={{ top: 6, right: 10, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                <XAxis dataKey="label" tick={{ fontSize: 9 }} angle={-30} textAnchor="end" height={40}/>
                <YAxis tick={{ fontSize: 10 }}/>
                <Tooltip/>
                <Bar dataKey="count" fill="#2563EB" radius={[4, 4, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-[13px] font-bold text-slate-900 mb-2">Évolution nombre de corners (12 mois)</h3>
          <div className="h-52">
            <ResponsiveContainer>
              <LineChart data={evo}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                <XAxis dataKey="mois" tick={{ fontSize: 10 }}/>
                <YAxis tick={{ fontSize: 10 }}/>
                <Tooltip/>
                <Line type="monotone" dataKey="count" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3 }}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="infomag-recap-teams">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="text-[13px] font-bold text-slate-900">Équipes rattachées</h3>
          <p className="text-[10px] text-slate-500">{teams.length} équipes · DR / BM et leur périmètre</p>
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left text-[9px] font-bold uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">DR</th>
                <th className="px-3 py-2">BM</th>
                <th className="px-3 py-2 text-right">Corners</th>
                <th className="px-3 py-2 text-right">ETP</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((t, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="px-3 py-1.5 font-semibold text-slate-900">{t.dr || "—"}</td>
                  <td className="px-3 py-1.5 text-slate-700">{t.bm || "—"}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{t.n_corners}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtNum(t.etp, 1)}</td>
                </tr>
              ))}
              {teams.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400 italic">Aucune équipe.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ── Sparkline (inline SVG) ── */
function Sparkline({ data, color = "#2563EB" }) {
  if (!data || data.length < 2) return <span className="text-slate-300">—</span>;
  const vals = data.map(d => d.value || 0);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const w = 60, h = 20;
  const step = w / (vals.length - 1);
  const pts = vals.map((v, i) => `${i * step},${h - ((v - min) / range) * h}`).join(" ");
  return (
    <svg width={w} height={h} className="inline-block">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5"/>
    </svg>
  );
}

/* ── Tab 4 : Indicateurs — recap cards + table ── */
function IndicateursTab({ rows, onSelect }) {
  const totals = useMemo(() => {
    const t = rows.reduce((a, r) => ({
      n_visites: a.n_visites + (r.n_visites || 0),
      n_audits:  a.n_audits  + (r.n_audits  || 0),
      n_rdv:     a.n_rdv     + (r.n_rdv     || 0),
      v3m:       a.v3m       + (r.visites_3m || 0),
      cible3m:   a.cible3m   + (r.visites_cible_3m || 6),
      alertes:   a.alertes   + (r.n_alertes_open || 0),
      plans_o:   a.plans_o   + (r.n_plans_open || 0),
      plans_c:   a.plans_c   + (r.n_plans_closed || 0),
      delai_sum: a.delai_sum + (r.delai_moyen_j || 0),
      delai_n:   a.delai_n   + (r.delai_moyen_j ? 1 : 0),
    }), { n_visites:0, n_audits:0, n_rdv:0, v3m:0, cible3m:0, alertes:0, plans_o:0, plans_c:0, delai_sum:0, delai_n:0 });
    return { ...t,
      freq:  t.cible3m ? t.v3m / t.cible3m : 0,
      delai: t.delai_n ? t.delai_sum / t.delai_n : null,
    };
  }, [rows]);
  const t = totals;
  const tFreq = t.freq;
  const stats = useMemo(() => rows.reduce((a, r) => ({
    n_corners: a.n_corners + 1,
    n_visites: a.n_visites + (r.n_visites || 0),
    n_audits:  a.n_audits  + (r.n_audits  || 0),
    n_rdv:     a.n_rdv     + (r.n_rdv     || 0),
    n_alertes: a.n_alertes + (r.n_alertes_open || 0),
    n_plans_open: a.n_plans_open + (r.n_plans_open || 0),
  }), { n_corners: 0, n_visites: 0, n_audits: 0, n_rdv: 0, n_alertes: 0, n_plans_open: 0 }), [rows]);
  const cards = [
    { icon: Store,        label: "Magasins",       value: stats.n_corners, color: "text-blue-600 bg-blue-50", testid: "infomag-indic-card-magasins" },
    { icon: BarChart3,    label: "Visites",        value: stats.n_visites, color: "text-emerald-600 bg-emerald-50", testid: "infomag-indic-card-visites" },
    { icon: Users,        label: "RDV",            value: stats.n_rdv,     color: "text-purple-600 bg-purple-50", testid: "infomag-indic-card-rdv" },
    { icon: AlertTriangle,label: "Alertes ouvertes", value: stats.n_alertes, color: "text-rose-600 bg-rose-50", testid: "infomag-indic-card-alertes" },
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="infomag-indic-cards">
        {cards.map(c => (
          <div key={c.label} className="bg-white rounded-xl border border-slate-200 p-4 shadow-[0_1px_3px_rgba(15,23,42,.04)]" data-testid={c.testid}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{c.label}</span>
              <span className={`w-7 h-7 rounded-md grid place-items-center ${c.color}`}><c.icon className="w-3.5 h-3.5"/></span>
            </div>
            <div className="text-[22px] font-bold text-slate-900 tabular-nums">{fmtNum(c.value)}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">sur {stats.n_corners} magasins filtrés</div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="infomag-indic-table">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
        <h2 className="text-[14px] font-bold text-slate-900">Indicateurs par corner</h2>
        <span className="text-[11px] text-slate-500">{rows.length} corners</span>
      </div>
      <div className="overflow-auto max-h-[600px]">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 z-10 bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[9px] font-bold uppercase tracking-wider text-slate-500">
              <th className="px-2 py-2">Corner / Compte</th>
              <th className="px-2 py-2">Enseigne</th>
              <th className="px-2 py-2">DR / BM</th>
              <th className="px-2 py-2 text-right">Visites</th>
              <th className="px-2 py-2 text-right">Audits</th>
              <th className="px-2 py-2 text-right">RDV</th>
              <th className="px-2 py-2">Dernier RDV</th>
              <th className="px-2 py-2">Dern. demande</th>
              <th className="px-2 py-2 text-center">Réal./Cible (3m)</th>
              <th className="px-2 py-2 text-right">Fréq.</th>
              <th className="px-2 py-2 text-center">Tendance</th>
              <th className="px-2 py-2 text-right">Alertes</th>
              <th className="px-2 py-2 text-right">Plans O/C</th>
              <th className="px-2 py-2 text-right">Délai (j)</th>
              <th className="px-2 py-2"></th>
            </tr>
            {rows.length > 0 && (
              <tr className="text-[11px] font-bold text-slate-900 bg-blue-50 border-b-2 border-blue-200" data-testid="infomag-indic-total-row">
                <td className="px-2 py-2" colSpan={3}>TOTAL ({rows.length} magasins)</td>
                <td className="px-2 py-2 text-right tabular-nums text-blue-700">{fmtNum(t.n_visites)}</td>
                <td className="px-2 py-2 text-right tabular-nums text-emerald-700">{fmtNum(t.n_audits)}</td>
                <td className="px-2 py-2 text-right tabular-nums text-purple-700">{fmtNum(t.n_rdv)}</td>
                <td className="px-2 py-2" colSpan={2}>—</td>
                <td className="px-2 py-2 text-center">
                  <span className={`inline-flex items-baseline gap-0.5 tabular-nums font-bold text-[11px] px-1.5 py-0.5 rounded ${tFreq >= 1 ? "bg-emerald-100 text-emerald-700" : tFreq >= 0.6 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>
                    {fmtNum(t.v3m)}<span className="text-slate-400">/</span><span className="text-slate-500 text-[10px]">{fmtNum(Math.round(t.cible3m))}</span>
                  </span>
                </td>
                <td className={`px-2 py-2 text-right tabular-nums ${tFreq >= 0.9 ? "text-emerald-700" : tFreq >= 0.6 ? "text-amber-700" : "text-rose-700"}`}>{fmtPct(tFreq)}</td>
                <td className="px-2 py-2 text-center">—</td>
                <td className={`px-2 py-2 text-right tabular-nums ${t.alertes > 0 ? "text-rose-700" : "text-slate-500"}`}>{fmtNum(t.alertes)}</td>
                <td className="px-2 py-2 text-right tabular-nums text-slate-700">{fmtNum(t.plans_o)} / {fmtNum(t.plans_c)}</td>
                <td className="px-2 py-2 text-right tabular-nums text-slate-600">{t.delai != null ? Math.round(t.delai) : "—"}</td>
                <td/>
              </tr>
            )}
          </thead>
          <tbody>
            {rows.map(r => {
              const freq = r.freq_score || 0;
              return (
                <tr key={r.corner_key} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="px-2 py-1.5 font-semibold text-slate-900">
                    <div>{r.corner || "—"}</div>
                    {r.nom_compte && r.nom_compte !== r.corner && (
                      <div className="text-[9px] text-slate-400 font-normal">{r.nom_compte}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-slate-600 truncate max-w-[100px]">{r.enseigne || "—"}</td>
                  <td className="px-2 py-1.5 text-slate-600 text-[10px]">
                    <div>{r.dr || "—"}</div>
                    <div className="text-slate-400">{r.bm || "—"}</div>
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-blue-700">{r.n_visites}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-emerald-700">{r.n_audits}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-purple-700">{r.n_rdv}</td>
                  <td className="px-2 py-1.5 text-slate-600">{fmtDate(r.last_rdv)}</td>
                  <td className="px-2 py-1.5 text-slate-600">{fmtDate(r.last_demande_rdv)}</td>
                  <td className="px-2 py-1.5 text-center">
                    <span className={`inline-flex items-baseline gap-0.5 tabular-nums font-semibold text-[11px] px-1.5 py-0.5 rounded ${freq >= 1 ? "bg-emerald-100 text-emerald-700" : freq >= 0.6 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}
                          title={`${r.visites_3m || 0} visites sur 3 mois glissants (cible ${r.visites_cible_3m || 6})`}>
                      <span className="font-bold">{r.visites_3m || 0}</span>
                      <span className="text-slate-400">/</span>
                      <span className="text-slate-500 text-[10px]">{r.visites_cible_3m || 6}</span>
                    </span>
                  </td>
                  <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${freq >= 0.9 ? "text-emerald-700" : freq >= 0.6 ? "text-amber-700" : "text-rose-700"}`}>{fmtPct(freq)}</td>
                  <td className="px-2 py-1.5 text-center"><Sparkline data={r.ca_sparkline}/></td>
                  <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${(r.n_alertes_open||0) > 0 ? "text-rose-700" : "text-slate-500"}`}>{r.n_alertes_open || 0}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{r.n_plans_open || 0} / {r.n_plans_closed || 0}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">{r.delai_moyen_j ? Math.round(r.delai_moyen_j) : "—"}</td>
                  <td className="px-2 py-1.5">
                    <button onClick={() => onSelect(r)}
                            data-testid={`infomag-indic-view-${r.corner_key}`}
                            className="flex items-center gap-1 text-[10px] font-semibold text-blue-600 hover:underline">
                      <Eye className="w-3 h-3"/> Voir fiche
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={15} className="px-4 py-8 text-center text-slate-400 italic">Aucun corner pour ces filtres.</td></tr>}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}

/* ── Store detail drawer (reuses StoreDetail page in a side panel) ── */
function StoreDetailDrawer({ corner_key, onClose }) {
  return (
    <>
      <div className="fixed inset-0 bg-slate-900/40 z-40" onClick={onClose} data-testid="infomag-drawer-backdrop"/>
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-[1000px] bg-[#F7F9FC] shadow-2xl z-50 flex flex-col" data-testid="infomag-drawer">
        <div className="flex items-center gap-3 px-6 py-3 border-b border-slate-200 bg-white">
          <div className="w-8 h-8 rounded-lg bg-blue-600 text-white grid place-items-center"><Store className="w-4 h-4"/></div>
          <h2 className="text-[15px] font-bold text-slate-900 flex-1">Fiche magasin</h2>
          <button onClick={onClose} data-testid="infomag-drawer-close" className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center text-slate-500">
            <XIcon className="w-4 h-4"/>
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          <StoreDetailErrorBoundary corner_key={corner_key}>
            <StoreDetail idOverride={corner_key} embedded/>
          </StoreDetailErrorBoundary>
        </div>
      </div>
    </>
  );
}

/* Local error boundary so the drawer never breaks the whole page */
class StoreDetailErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch() {}
  render() {
    if (this.state.err) {
      return (
        <div className="bg-white rounded-xl border border-rose-200 p-6 text-center">
          <div className="text-sm font-bold text-rose-700 mb-1">Impossible d'afficher la fiche</div>
          <div className="text-xs text-slate-600">Store ID : <code>{this.props.corner_key}</code></div>
          <div className="text-xs text-slate-500 mt-2">{String(this.state.err?.message || this.state.err)}</div>
          <a href={`/stores/${this.props.corner_key}`} className="mt-3 inline-block text-xs font-semibold text-blue-600 hover:underline">Ouvrir en pleine page →</a>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ═══════════════════════ MAIN COMPONENT ═══════════════════════ */
export default function InfoMag() {
  const [sp, setSp] = useSearchParams();
  const initTab = sp.get("tab") || "recap";
  const [tab, setTab] = useState(initTab);
  const [f, setF] = useState({});
  const [meta, setMeta] = useState(null);
  const [indicators, setIndicators] = useState(null);
  const [drawerCK, setDrawerCK] = useState(null);

  useEffect(() => { api.get("/kpi/filters").then(r => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const params = {};
    ["drs", "bms", "secteurs", "corners", "enseignes", "marques", "annees", "mois"].forEach(k => {
      if (f[k]?.length) params[k] = f[k].join(",");
    });
    if (f.search) params.q = f.search;
    api.get("/info-mag/indicators", { params })
      .then(r => setIndicators(r.data))
      .catch(() => setIndicators(null));
  }, [f]);

  const filteredIndic = useMemo(() => {
    const rows = indicators?.rows || [];
    if (!f.search) return rows;
    const q = f.search.toLowerCase();
    return rows.filter(r => (r.corner || "").toLowerCase().includes(q) || (r.dr || "").toLowerCase().includes(q) || (r.bm || "").toLowerCase().includes(q));
  }, [indicators, f.search]);

  const tabs = [
    { k: "recap",    label: "Récap",       icon: LayoutGrid },
    { k: "magasins", label: "Magasins",    icon: Store },
    { k: "base",     label: "Base Info",   icon: Database },
    { k: "indic",    label: "Indicateurs", icon: BarChart3 },
  ];

  const goTab = k => { setTab(k); setSp({ tab: k }); };

  return (
    <div className="min-h-screen bg-[#F7F9FC]" data-testid="infomag-page">
      <div className="px-4 py-4 space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-600 text-white grid place-items-center">
            <Store className="w-5 h-5"/>
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 uppercase leading-none">Info-Mag</h1>
            <p className="text-[11px] text-slate-500 mt-0.5">Vue 360° du réseau : magasins, équipes, données de référence et indicateurs terrain</p>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex items-center gap-1 border-b border-slate-200">
          {tabs.map(t => {
            const Icon = t.icon; const active = tab === t.k;
            return (
              <button key={t.k} data-testid={`infomag-tab-${t.k}`} onClick={() => goTab(t.k)}
                      className={`flex items-center gap-1.5 px-4 py-2 text-[13px] font-semibold border-b-2 transition ${active ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
                <Icon className="w-3.5 h-3.5"/> {t.label}
              </button>
            );
          })}
        </div>

        {/* Filter bar (common) */}
        <InfoMagFilterBar meta={meta} f={f} setF={setF}/>

        {/* Tab content */}
        {tab === "recap"    && <RecapTab data={indicators}/>}
        {tab === "magasins" && <div data-testid="infomag-tab-content-magasins"><Stores/></div>}
        {tab === "base"     && <div data-testid="infomag-tab-content-base"><BaseInfo/></div>}
        {tab === "indic"    && <IndicateursTab rows={filteredIndic} onSelect={r => setDrawerCK(r.corner_key)}/>}

        {drawerCK && <StoreDetailDrawer corner_key={drawerCK} onClose={() => setDrawerCK(null)}/>}
      </div>
    </div>
  );
}
